<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import {
  hasActiveAiAgent,
  resolveAiAgentMode,
  SUGGEST_MODES,
} from 'dashboard/helper/aiAgentMode';
import HeaderAssigneePill from './HeaderAssigneePill.vue';
import HeaderWindowPill from './HeaderWindowPill.vue';

/**
 * Zona de exceção do cabeçalho (V3, mockup final-v3v2.html).
 *
 * O topo só mostra o que exige decisão AGORA. Estado em ordem não aparece: é
 * isso que faz a cor no cabeçalho voltar a significar "aja". Duas exceções no
 * máximo, por prioridade; a terceira vira ponto no trilho da direita, porque
 * cabeçalho com três avisos é exatamente o problema que esta tela está matando.
 *
 * A largura é reservada mesmo vazia, então o Resolver não dança quando um aviso
 * entra ou sai.
 */
const props = defineProps({
  chat: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  // Vem do cabeçalho: a janela de atendimento fechada é a exceção mais grave,
  // porque sem ela não dá nem para responder.
  windowClosed: { type: Boolean, default: false },
  // Sem fixado ao lado, a zona reserva largura para o Resolver não pular quando
  // uma exceção aparece. Com fixado ao lado, a reserva virava um buraco entre
  // o último fixado e o Resolver (Lucas, 01/09): aí ela encolhe ao conteúdo.
  reservar: { type: Boolean, default: true },
});

// Um popover por vez dentro da zona, como no resto do cabeçalho.
const abertoNo = ref(null);
const alternar = chave => {
  abertoNo.value = abertoNo.value === chave ? null : chave;
};
const fecharPopover = () => {
  abertoNo.value = null;
};

const { t } = useI18n();
const store = useStore();
const alternando = ref(false);

const iaPausada = computed(
  () =>
    props.chat?.additional_attributes?.ai_agent?.paused === true ||
    props.chat?.ai_agent_paused === true
);
const temIa = computed(
  () => props.chat?.ai_agent_active !== false && hasActiveAiAgent(props.chat)
);
// Assistente só de sugestão (Lia): pausar não cala resposta nenhuma, só para de
// rascunhar. A copy do chip tem que dizer isso, senão "IA pausada" assusta.
const iaSoSugere = computed(() =>
  SUGGEST_MODES.includes(resolveAiAgentMode(props.chat))
);
const chaveIa = sufixo =>
  `CONVERSATION.HEADER.EXCEPTIONS.${iaSoSugere.value ? 'AI_SUGGEST_' : 'AI_'}${sufixo}`;
const semResponsavel = computed(() => !props.chat?.meta?.assignee?.id);

// Prioridade: não poder responder > ninguém dono > IA parada.
const excecoes = computed(() => {
  const lista = [];
  if (props.windowClosed) lista.push('janela');
  if (semResponsavel.value) lista.push('responsavel');
  if (temIa.value && iaPausada.value) lista.push('ia');
  return lista;
});

const visiveis = computed(() => excecoes.value.slice(0, 2));
const compacto = computed(() => visiveis.value.length > 1);

const retomarIa = async () => {
  if (alternando.value || !props.chat?.id) return;
  alternando.value = true;
  try {
    // keep_status: retomar pelo cabeçalho religa a IA e NÃO fecha a conversa.
    // Dentro do painel, devolver pra IA segue resolvendo, como sempre foi.
    const { data: estado } = await ConversationAiAgentAPI.update(
      props.chat.id,
      {
        paused: false,
        keep_status: true,
      }
    );
    const attrs = props.chat.additional_attributes || {};
    store.dispatch('updateConversation', {
      ...props.chat,
      additional_attributes: {
        ...attrs,
        ai_agent: { ...(attrs.ai_agent || {}), paused: estado.paused === true },
      },
      ai_agent_paused: estado.paused === true,
      updated_at: estado.conversation_updated_at || new Date().toISOString(),
    });
    useAlert(t('CONVERSATION.HEADER.EXCEPTIONS.AI_RESUMED'));
  } catch {
    useAlert(t('CONVERSATION.HEADER.EXCEPTIONS.AI_RESUME_FAILED'));
  } finally {
    alternando.value = false;
  }
};
</script>

<template>
  <!-- Largura reservada mesmo sem exceção (só sem fixados): o Resolver não muda de lugar. -->
  <div
    class="flex shrink-0 items-center justify-end gap-2"
    :class="reservar ? 'min-w-[150px]' : 'min-w-0'"
    data-testid="header-exception-zone"
    :data-reserva="reservar ? 'sim' : 'nao'"
  >
    <HeaderWindowPill
      v-if="visiveis.includes('janela')"
      :chat="chat"
      :inbox="inbox"
      :open="abertoNo === 'janela'"
      data-testid="exception-window"
      @toggle="alternar('janela')"
      @close="fecharPopover"
    />

    <HeaderAssigneePill
      v-if="visiveis.includes('responsavel')"
      :conversation-id="chat.id"
      :inbox-id="chat.inbox_id"
      :assignee="null"
      :compact="compacto"
      :open="abertoNo === 'responsavel'"
      data-testid="exception-assignee"
      @toggle="alternar('responsavel')"
      @close="fecharPopover"
    />

    <button
      v-if="visiveis.includes('ia')"
      v-tooltip.bottom="t(chaveIa('PAUSED_HINT'))"
      type="button"
      data-testid="exception-ai"
      class="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-n-amber-8/40 bg-n-amber-9/15 px-3 text-[12px] font-semibold text-n-amber-11"
      :class="{ 'opacity-60': alternando }"
      :disabled="alternando"
      @click="retomarIa"
    >
      <span class="i-ri-pause-circle-line size-3.5" aria-hidden="true" />
      <span v-if="!compacto"> {{ t(chaveIa('PAUSED_LABEL')) }} · </span>
      <b>{{ t(chaveIa('RESUME_ACTION')) }}</b>
    </button>
  </div>
</template>
